import React, { useEffect, useState, useContext } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { AuthContext } from '../../contexts/AuthContext';
import { SubscriptionContext } from '../../contexts/SubscriptionContext';
import { useAnalytics } from '../../contexts/AnalyticsContext';
import { useMetaPixel } from '../../contexts/MetaPixelContext';
import { useTranslation } from 'react-i18next';
import { FaCheckCircle, FaSpinner, FaBook, FaTachometerAlt, FaStar } from 'react-icons/fa';
import api from '../../services/api';
import { toast } from 'react-toastify';
import Logo from '../../images/logo.svg';
import arabicBg from '../../images/arabic-bg-dark.svg';
import './style.css';

const PaymentSuccessPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useContext(AuthContext);
  const { refreshStatus } = useContext(SubscriptionContext);
  const { trackSubscriptionStarted, trackError } = useAnalytics();
  const { trackPurchase } = useMetaPixel();
  const [countdown, setCountdown] = useState(5);
  const [processing, setProcessing] = useState(true);

  useEffect(() => {
    const verifyPayment = async () => {
      const sessionId = searchParams.get('session_id');

      if (sessionId) {
        try {
          // Verify and process the payment session
          const response = await api.post('/payments/verify-session', null, {
            params: { session_id: sessionId }
          });

          // Refresh subscription status after processing
          await refreshStatus();

          // Track successful subscription
          if (user && response.data) {
            trackSubscriptionStarted({
              userId: user.id,
              tier: 'premium',
              amount: response.data.amount || 49,
              currency: response.data.currency || 'EUR',
              interval: response.data.interval || 'year',
            });
            // Meta Pixel: Track Purchase for ads attribution
            trackPurchase({
              value: response.data.amount || 49,
              currency: response.data.currency || 'EUR',
              contentName: 'Annual Subscription',
            });
          }

          setProcessing(false);
        } catch (error) {
          console.error('Error verifying payment:', error);

          // If already processed, just refresh status
          if (error.response?.data?.error?.code === 'PAYMENT_NOT_COMPLETED') {
            trackError('payment_not_completed', 'Payment not completed', {
              session_id: sessionId,
            });
            toast.error(t('paymentSuccess.paymentNotCompleted'));
            navigate('/subscribe');
            return;
          }

          // Track verification error
          trackError('payment_verification_failed', error.message, {
            session_id: sessionId,
          });

          // For other errors, still try to refresh status
          await refreshStatus();
          setProcessing(false);
        }
      } else {
        // No session ID, just refresh status
        await refreshStatus();
        setProcessing(false);
      }
    };

    verifyPayment();
  }, [searchParams, refreshStatus, navigate, t]);

  useEffect(() => {
    if (processing) return;

    // Countdown timer
    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          navigate('/dashboard');
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [navigate, processing]);

  return (
    <div className="payment-success-page">
      {/* Arabic Background Pattern */}
      <div className="payment-success-bg-pattern" style={{ backgroundImage: `url(${arabicBg})` }} />

      {/* Header */}
      <div className="payment-success-header">
        <Link to="/" className="payment-success-logo">
          <img src={Logo} alt={t('verifyEmail.logoAlt')} />
        </Link>
      </div>

      {/* Main Content */}
      <div className="payment-success-content">
        <div className="success-card">
          {processing ? (
            <>
              <div className="status-icon-wrapper processing">
                <FaSpinner className="status-icon spinning" />
              </div>
              <h1>{t('paymentSuccess.processingPayment')}</h1>
              <p className="processing-text">
                {t('paymentSuccess.pleaseWait')}
              </p>
            </>
          ) : (
            <>
              <div className="status-icon-wrapper success">
                <FaCheckCircle className="status-icon" />
              </div>

              <h1>{t('paymentSuccess.successTitle')}</h1>

              <p className="success-message">
                {t('paymentSuccess.subscriptionActive')}
              </p>

              <div className="welcome-badge">
                <FaStar className="star-icon" />
                <span>{t('paymentSuccess.welcomeAboard')}</span>
                <FaStar className="star-icon" />
              </div>

              <div className="redirect-info">
                <p>{t('paymentSuccess.redirecting')}</p>
              </div>

              <button
                onClick={() => navigate('/dashboard')}
                className="btn-dashboard"
              >
                {t('paymentSuccess.goToDashboard')}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default PaymentSuccessPage;
